<template>
    <v-container fluid>
        <v-row align="center">
            <v-col cols="12" md="6" lg="4">
                <v-card variant="flat" class="pa-2">
                    <v-card-title class="text-h6 text-center">Security Code</v-card-title>
                    <v-alert color="info" icon="$info" title="Security Code" variant="tonal"
                        text="Please enter the 6 digit code from your authenticator app"></v-alert>
                    <v-card-text>
                        <v-form ref="formRef" @submit.prevent="submit">
                            <div class="d-flex justify-center mb-4">
                                <v-text-field v-for="(digit, index) in otpDigits" :key="index"
                                    v-model="otpDigits[index]" maxlength="1" outlined dense class="otp-input mx-1"
                                    @input="handleInput(index)" @keydown="handleKeydown($event, index)"
                                    @paste="handlePaste($event, index)" ref="otpRefs"></v-text-field>
                            </div>
                            <v-checkbox v-model="rememberDevice" label="Remember this device for 30 days"></v-checkbox>
                            <v-alert v-if="error" type="error" dense class="my-2">{{ error }}</v-alert>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <!-- <v-btn text @click="cancel">Cancel</v-btn> -->
                        <v-btn color="secondary" :loading="isSubmitting" @click="submit" variant="tonal" rounded="xs">
                            Verify </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePatientStore } from '../stores/patient'
import { twoFactorVerify } from '../api/auth'
import { useOtpInput } from '../composables/useOtpInput'

const router = useRouter()
const patientStore = usePatientStore()

const { otpDigits, otpRefs, otp, handlePaste, handleInput, handleKeydown, focusFirst } = useOtpInput()
const recoveryCode = ref('')
const rememberDevice = ref(false)
const isSubmitting = ref(false)
const error = ref('')

onMounted(async () => {
    await focusFirst()
})

const submit = async () => {
    error.value = ''

    if (!otp.value && !recoveryCode.value) {
        error.value = 'Please enter your authenticator code or a recovery code.'
        return
    }

    isSubmitting.value = true
    try {
        const payload: any = {}
        if (otp.value) payload.otp = otp.value.trim()
        payload.remember_device = rememberDevice.value

        const response = await twoFactorVerify(payload)

        const result = response.data

        const token = result.access_token
        if (!token) throw new Error('No access token returned from 2FA verify')

        localStorage.setItem('patient_token', token)
        if (result.device_token) {
            try {
                localStorage.setItem('patient_device_token', result.device_token)
            } catch (e) {
                console.warn('Could not store device token', e)
            }
        }

        // Hydrate store and navigate
        await patientStore.ensurePatient()
        router.push('/dashboard')
    } catch (err: any) {
        console.error('2FA verify failed')
        const status = err?.response?.status
        if (status === 400) {
            error.value = 'Invalid code. Please try again.'
        } else {
            error.value = err?.response?.data?.message || err?.message || 'Verification failed'
        }
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.v-container {
    min-height: 100vh;
}

.otp-input {
    width: 42px;
    text-align: center;
}

.otp-input :deep(.v-field__input) {
    font-size: 1.5rem;
    font-weight: bold;
    padding: 0.9rem;
}

:deep(.v-alert-title) {
    font-size: 1rem;
}
</style>